/**
 * One pane in a multi-viewport layout.
 *
 * Owns the camera, view and controls for a single rectangular region of the
 * shared canvas. Lifetimes are managed by `MultiViewLayout`.
 */

import * as THREE from '../../../libs/three.js/build/three.module.js';
import { View } from '../View.js';
import { PaneOrbitControls } from './PaneOrbitControls.js';

export class PaneConfig {
  /**
   * @param {object} opts
   * @param {string} opts.id                Stable identifier (e.g. 'top', 'persp', 'ns', 'ew').
   * @param {string} opts.kind              Logical kind, same set as id for the alignment use case.
   * @param {{x:number,y:number,w:number,h:number}} opts.viewportFraction
   *                                        Fractional viewport rect in [0,1] of the render area.
   *                                        Origin (0,0) is bottom-left (WebGL convention).
   * @param {'orthographic'|'perspective'} opts.cameraType
   * @param {boolean} [opts.lockRotation]   If true, yaw/pitch deltas from drag are suppressed.
   * @param {object}  [opts.initialView]    Optional patch applied to the new View
   *                                        (position/yaw/pitch/radius).
   * @param {(viewer:object)=>void} [opts.beforeRender]
   *                                        Optional hook invoked once per frame, just before
   *                                        this pane renders. Lets the caller adjust shared
   *                                        scene state per pane (e.g. switch a material's
   *                                        colour mode). Keep it cheap — it runs every frame.
   */
  constructor(opts) {
    this.id = opts.id;
    this.kind = opts.kind ?? opts.id;
    this.viewportFraction = opts.viewportFraction;
    this.cameraType = opts.cameraType;
    this.lockRotation = !!opts.lockRotation;
    this.initialView = opts.initialView ?? null;
    this.beforeRender = typeof opts.beforeRender === 'function' ? opts.beforeRender : null;

    // Populated when the pane is attached to a layout/viewer:
    this.camera = null;     // THREE.OrthographicCamera | THREE.PerspectiveCamera
    this.view = null;       // independent View instance
    this.controls = null;   // PaneOrbitControls
  }

  /**
   * Attach this pane to a viewer, creating camera + view + controls.
   * Idempotent — calling again is a no-op.
   */
  attach(viewer) {
    if (this.view) return;

    this.view = new View();
    if (this.initialView) {
      Object.assign(this.view, this.initialView);
    }

    if (this.cameraType === 'orthographic') {
      this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1_000_000);
    } else {
      this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 1_000_000);
    }

    this.controls = new PaneOrbitControls(viewer, {
      view: this.view,
      camera: this.camera,
      lockRotation: this.lockRotation,
    });
    this.controls.boundPane = this;
  }

  detach() {
    this.camera = null;
    this.view = null;
    this.controls = null;
  }

  /** Pixel rect (origin bottom-left, GL convention) for the given canvas size. */
  pixelViewport(canvasWidth, canvasHeight) {
    const f = this.viewportFraction;
    return {
      x: Math.round(f.x * canvasWidth),
      y: Math.round(f.y * canvasHeight),
      w: Math.round(f.w * canvasWidth),
      h: Math.round(f.h * canvasHeight),
    };
  }

  /**
   * Apply this pane's view to its camera. Mirrors the Z-up convention used by
   * viewer.js (rotation order ZXY, rotation.x = π/2 + pitch, rotation.z = yaw).
   * Called from the multi-view render path each frame.
   */
  applyViewToCamera() {
    const view = this.view;
    const camera = this.camera;
    if (!view || !camera) return;
    camera.position.copy(view.position);
    camera.rotation.order = 'ZXY';
    camera.rotation.x = Math.PI / 2 + view.pitch;
    camera.rotation.z = view.yaw;
    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
  }

  /**
   * Update the camera's projection matrix based on the current viewport pixel
   * size and the view's radius (for ortho) or default fov (for perspective).
   */
  updateProjection(viewportPixels) {
    const camera = this.camera;
    const view = this.view;
    if (!camera || !view || viewportPixels.w === 0 || viewportPixels.h === 0) return;

    const aspect = viewportPixels.w / viewportPixels.h;

    if (this.cameraType === 'orthographic') {
      const r = view.radius;
      camera.left = -r;
      camera.right = r;
      camera.top = r / aspect;
      camera.bottom = -r / aspect;
    } else {
      camera.aspect = aspect;
    }
    camera.updateProjectionMatrix();
  }
}
